/**
 * B090013 在庫照会(入荷予定)(本社業務)
 */

import { Column, Query } from "@material-table/core";
import { Box } from "@mui/material";
import { B090013Find, B090013FindRequest, catchRestError } from "clients/rest";
import { msgState, SFrame, SLabel, STable, STextField } from "components";
import { createRef, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mapIds } from "services";
import { focusState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  sokoCd: string;
  sokoNmr: string;
  shohinCd: string;
  shohinNmr: string;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  sokoCd: string;
  sokoNmr: string;
  shohinCd: string;
  shohinNmr: string;
}>;

// テーブルデータ定義
type Data = Partial<{
  gyoNo: number;
  juchudenpyoNo: number;
  hachudenpyoNo: number;
  shireCd: string;
  shireNmr: string;
  hachusu: number;
  shirezumisu: number;
  nyukayoteisu: number;
  nyukayoteiYmd: number;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  sokoCd: "",
  sokoNmr: "",
  shohinCd: "",
  shohinNmr: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "2rem", align: "right" },
  { field: "juchudenpyoNo", title: "受注No.", width: "7rem" },
  { field: "hachudenpyoNo", title: "発注No.", width: "7rem" },
  { field: "shireCd", title: "仕入先ｺｰﾄﾞ", width: "6rem" },
  { field: "shireNmr", title: "仕入先名", width: "15rem" },
  {
    field: "hachusu",
    title: "発注数　",
    width: "6rem",
    align: "right",
    render: (data) => data.hachusu?.toLocaleString(),
  },
  {
    field: "shirezumisu",
    title: "入荷済数　",
    width: "8rem",
    align: "right",
    render: (data) => data.shirezumisu?.toLocaleString(),
  },
  {
    field: "nyukayoteisu",
    title: "入荷予定数　",
    width: "8rem",
    align: "right",
    render: (data) => data.nyukayoteisu?.toLocaleString(),
  },
  { field: "nyukayoteiYmd", title: "入荷予定日", width: "9rem" },
];

const focusId = "B090013";

// B090013 在庫照会(入荷予定)(本社業務)
export const B090013 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();

  // 初期表示イベント
  useEffect(() => {
    // 処理詳細１
    // 倉庫コードセット
    if (props.sokoCd) {
      methods.setValue("sokoCd", props.sokoCd);
    }
    // 倉庫名表示
    if (props.sokoNmr) {
      methods.setValue("sokoNmr", props.sokoNmr);
    }
    // 商品コードセット
    if (props.shohinCd) {
      methods.setValue("shohinCd", props.shohinCd);
    }
    // 商品名表示
    if (props.shohinNmr) {
      methods.setValue("shohinNmr", props.shohinNmr);
    }
    // 検索実行
    (tableRef.current as any).onQueryChange();
    // 初回フォーカス先
    focus(1);
  });

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 検索フォームのデータを取得する。
      const fields = methods.getValues();
      // 出力順の設定
      let order = ["nyukayoteiYmd,asc"];

      // 検索パラメータを作成する。
      const param: B090013FindRequest = {
        sokoCd: fields.sokoCd!,
        shohinCd: fields.shohinCd!,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: order,
      };
      // API呼び出し
      B090013Find(param)
        // 正常取得時
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        });
    });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame title="在庫照会(入荷予定)" gridTemplateColumns="6rem 12rem 12rem">
        <SLabel>倉庫名</SLabel>
        <STextField label="倉庫名" name="sokoNmr" disabled />
        <Box />
        <SLabel>商品名</SLabel>
        <STextField
          label="商品名"
          name="shohinNmr"
          gridColumn="span 2"
          disabled
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          height={280}
          width={1200}
        />
      </SFrame>
    </FormProvider>
  );
};
